<template>
  <b-modal
    id="modal-import"
    :title="modalTitle"
    :size="step === 'upload' ? 'md' : 'xl'"
    centered
    hide-footer
    no-close-on-backdrop
    @hidden="resetModal"
  >
    <b-overlay :show="loading">
      <!-- ================= BƯỚC 1: CHỌN FILE ================= -->
      <div v-if="step === 'upload'">
        <label>Tải xuống file mẫu:</label>
        <button type="button" @click="downloadFile" style="color: #259abb">
          &nbsp;&nbsp;Template.xlsx
        </button>
        <form @submit.prevent="validateFile">
          <div class="py-4">
            <label for="file-upload">Import file</label>
            <div class="wrap__file mb-1">
              <span class="file__name">
                {{ file ? fileName : 'file import.xlsx' }}
              </span>
              <b-icon-upload
                scale="1.5"
                class="cursor-pointer"
                @click="$refs.fileUpload.click()"
              ></b-icon-upload>
              <input
                v-show="false"
                type="file"
                id="file-upload"
                ref="fileUpload"
                accept=".xlsx, .xls"
                @change="onUploadFile"
              />
            </div>
            <span v-if="fileError" class="text-danger">{{ fileError }}</span>
          </div>
          <div class="d-flex justify-content-end align-items-center mt-4">
            <b-button
              class="btn mr-2"
              type="button"
              @click="onClickCancelButton"
            >
              Hủy
            </b-button>
            <b-button
              class="btn btn-success ml-3"
              type="submit"
              :disabled="!file || !!fileError"
            >
              <span class="svg-icon">
                <inline-svg src="/media/svg/icons/Neolex/Basic/search.svg" />
              </span>
              Xem danh sách
            </b-button>
          </div>
        </form>
      </div>

      <!-- ================= BƯỚC 2: DUYỆT TRƯỚC KHI IMPORT ================= -->
      <div v-else-if="step === 'review'">
        <b-row class="mb-4">
          <b-col cols="3" class="d-flex">
            <div class="wrap__icon">
              <span class="svg-icon">
                <inline-svg src="/media/svg/Active-account/user-group.svg" />
              </span>
            </div>
            <div>
              <h6>Tổng số bản ghi</h6>
              <p>{{ allRows.length }}</p>
            </div>
          </b-col>
          <b-col cols="3" class="d-flex">
            <div class="wrap__icon">
              <span class="svg-icon">
                <inline-svg src="/media/svg/Active-account/check.svg" />
              </span>
            </div>
            <div>
              <h6>Hợp lệ / sẽ import</h6>
              <p>{{ validRows.length }} / {{ selectedRows.length }}</p>
            </div>
          </b-col>
          <b-col cols="3" class="d-flex">
            <div class="wrap__icon wrap__icon--error">
              <span class="svg-icon">
                <inline-svg src="/media/svg/Active-account/user-group.svg" />
              </span>
            </div>
            <div>
              <h6>Đang lỗi</h6>
              <p class="text-danger">{{ errorRows.length }}</p>
            </div>
          </b-col>
        </b-row>

        <!-- Tuỳ chọn áp cho cả danh sách -->
        <div class="import-options mb-3">
          <div class="import-options__label">Nhóm Zalo</div>
          <div class="zalo-cards">
            <div
              v-for="mode in zaloModeCards"
              :key="mode.value"
              class="zalo-card"
              :class="{
                'zalo-card--active': zaloMode === mode.value,
                'zalo-card--warn': mode.warn,
              }"
              role="button"
              tabindex="0"
              @click="zaloMode = mode.value"
              @keydown.enter.prevent="zaloMode = mode.value"
              @keydown.space.prevent="zaloMode = mode.value"
            >
              <div class="zalo-card__head">
                <component :is="mode.icon" class="zalo-card__icon" />
                <b-icon-check-circle-fill
                  v-if="zaloMode === mode.value"
                  class="zalo-card__check"
                />
              </div>
              <div class="zalo-card__title">
                {{ mode.title }}
                <span v-if="mode.tag" class="zalo-card__tag">{{
                  mode.tag
                }}</span>
              </div>
              <div class="zalo-card__desc">{{ mode.desc }}</div>
            </div>
          </div>

          <!-- Chọn nhóm cụ thể, chỉ hiện khi đã chọn "Nhóm chung" -->
          <div v-if="zaloMode === ZALO_MODE.SHARED" class="shared-picker">
            <label class="m-0 mr-2">Chọn nhóm:</label>
            <select
              class="form-control form-control-sm bulk-select"
              v-model="sharedGroupId"
              :disabled="loadingGroups"
            >
              <option :value="null">--- Chọn nhóm ---</option>
              <option
                v-for="group in zaloGroups"
                :key="group.group_id"
                :value="group.group_id"
              >
                {{ group.name }} ({{ group.total_member }} TV)
              </option>
            </select>
            <span v-if="loadingGroups" class="ml-2 text-muted">
              Đang tải danh sách nhóm...
            </span>
            <span v-else-if="!zaloGroups.length" class="ml-2 text-danger">
              Không lấy được nhóm Zalo nào
            </span>
            <span v-else-if="!sharedGroupId" class="ml-2 text-danger">
              Chưa chọn nhóm — không import được
            </span>
          </div>
          <div class="import-options__label mt-4">Sau khi import</div>
          <label class="opt-check">
            <input type="checkbox" v-model="autoAccept" :disabled="importing" />
            <span>
              <b>Tiếp nhận luôn</b>
              <small>Tạo tài khoản, kích hoạt gói và gán coach ngay.</small>
            </span>
          </label>
          <label
            class="opt-check opt-check--nested"
            :class="{ 'opt-check--off': !autoAccept }"
          >
            <input
              type="checkbox"
              v-model="sendNotification"
              :disabled="importing || !autoAccept"
            />
            <span>
              <b>Gửi thông báo cho khách</b>
              <small>
                Chỉ gửi cho khách chưa có tài khoản.
                {{ autoAccept ? '' : 'Cần bật "Tiếp nhận luôn".' }}
              </small>
            </span>
          </label>
          <label
            class="opt-check opt-check--nested"
            :class="{ 'opt-check--off': !autoAccept }"
          >
            <input
              type="checkbox"
              v-model="showWelcome"
              :disabled="importing || !autoAccept"
            />
            <span>
              <b>Hiển thị màn Welcome trên app</b>
              <small>
                Bỏ chọn thì khách không thấy màn Welcome sau khi kích hoạt gói.
                {{ autoAccept ? '' : 'Cần bật "Tiếp nhận luôn".' }}
              </small>
            </span>
          </label>
          <div class="import-options__summary">
            <b-icon-arrow-right-short />
            {{ importSummary }}
          </div>
        </div>

        <!-- Thao tác trên bảng -->
        <div class="bulk-bar mb-3">
          <div class="bulk-bar__group">
            <label class="checkbox-inline m-0 mr-3">
              <input type="checkbox" v-model="onlyErrors" />
              Chỉ hiện dòng lỗi
            </label>
          </div>
          <div class="bulk-bar__group">
            <b-button
              v-if="errorRows.length"
              size="sm"
              class="btn btn-secondary mr-2"
              :disabled="downloadingErrors"
              @click="downloadErrorRows"
            >
              <span class="svg-icon">
                <inline-svg src="/media/svg/icons/Neolex/File/file-download.svg" />
              </span>
              Tải {{ errorRows.length }} dòng lỗi
            </b-button>
            <b-button
              size="sm"
              :class="validated ? 'btn btn-secondary' : 'btn btn-success'"
              :disabled="!allRows.length"
              @click="revalidateRows"
            >
              {{ validated ? 'Kiểm tra lại' : 'Kiểm tra dữ liệu' }}
              {{ dirtyCount ? `(${dirtyCount} dòng đã sửa)` : '' }}
            </b-button>
          </div>
        </div>

        <b-alert :show="dirtyCount > 0" variant="warning" class="py-2">
          Bạn vừa sửa {{ dirtyCount }} dòng. Bấm <b>Kiểm tra lại</b> để hệ thống
          validate lại trước khi import.
        </b-alert>

        <div class="table-responsive review-table">
          <table class="table table-hover">
            <thead>
              <tr>
                <th class="text-center">
                  <input
                    type="checkbox"
                    :checked="allValidSelected"
                    :disabled="!validRows.length"
                    title="Chọn/bỏ chọn tất cả dòng hợp lệ"
                    @change="toggleAll($event.target.checked)"
                  />
                </th>
                <th class="text-center">STT</th>
                <th>Họ tên</th>
                <th>Số điện thoại</th>
                <th>Mã bệnh nhân</th>
                <th>Năm sinh</th>
                <th>Giới tính</th>
                <th>Danh xưng</th>
                <th>Nguồn tiếp nhận</th>
                <th>Phân loại bệnh</th>
                <th>Gói tham gia</th>
                <th>Coach phụ trách</th>
                <th>Nhóm onboard</th>
                <th>Trạng thái</th>
                <th>Chi tiết lỗi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!pagedRows.length">
                <td colspan="14" class="text-center">Không có dữ liệu</td>
              </tr>
              <import-preview-row
                v-for="row in pagedRows"
                :key="row.rowIndex"
                :row="row"
                :packages="packages"
                :coaches="coachOptions"
                @update="onRowUpdate(row, $event)"
                @toggle="onRowToggle(row, $event)"
              />
            </tbody>
          </table>
        </div>
        <pagination :paging="paging" />

        <div class="d-flex justify-content-end align-items-center mt-4">
          <div class="d-flex align-items-center">
            <b-button class="btn mr-2" type="button" @click="backToUpload">
              Chọn file khác
            </b-button>
            <b-button
              class="btn mr-2"
              type="button"
              @click="onClickCancelButton"
            >
              Hủy
            </b-button>
            <b-button
              class="btn btn-success ml-3"
              type="button"
              :title="
                !validated ? 'Phải bấm Kiểm tra dữ liệu trước khi import' : ''
              "
              :disabled="!validated || !selectedRows.length || dirtyCount > 0"
              @click="confirmImport"
            >
              <span class="svg-icon">
                <inline-svg src="/media/svg/icons/Neolex/Basic/save.svg" />
              </span>
              Import {{ selectedRows.length }} dòng
            </b-button>
          </div>
        </div>
      </div>

      <!-- ================= BƯỚC 3: TIẾN ĐỘ CHẠY NỀN ================= -->
      <div v-else-if="step === 'progress'" class="py-6 text-center">
        <template v-if="importing">
          <b-spinner variant="success" style="width: 3rem; height: 3rem" />
          <h5 class="mt-5">Đang import {{ importProgress.total }} dòng</h5>
          <p class="text-muted">
            Đã xử lý {{ importProgress.done }}/{{ importProgress.total }}
          </p>
        </template>
        <template v-else>
          <div class="wrap__icon mx-auto">
            <span class="svg-icon">
              <inline-svg src="/media/svg/Active-account/check.svg" />
            </span>
          </div>
          <h5 class="mt-5">Import xong</h5>
        </template>

        <b-progress
          :max="importProgress.total || 1"
          height="10px"
          class="mt-4 mb-5 mx-auto"
          style="max-width: 420px"
        >
          <b-progress-bar :value="importProgress.done" variant="success" />
        </b-progress>

        <b-row v-if="progress" class="justify-content-center">
          <b-col cols="3">
            <h6>Thành công</h6>
            <p class="text-success h4">{{ progress.success }}</p>
          </b-col>
          <b-col cols="3">
            <h6>Lỗi</h6>
            <p class="h4" :class="progress.failed ? 'text-danger' : ''">
              {{ progress.failed }}
            </p>
          </b-col>
        </b-row>

        <!-- Đối chiếu thật từ bảng tiếp nhận, chính xác hơn bộ đếm của job -->
        <b-row v-if="statusSummary" class="justify-content-center mt-4">
          <b-col cols="10">
            <div class="status-summary">
              <div>
                <span class="status-summary__num text-success">
                  {{ statusSummary.joined }}
                </span>
                đã tiếp nhận
              </div>
              <div>
                <span class="status-summary__num text-warning">
                  {{ statusSummary.imported }}
                </span>
                đã import, chưa tiếp nhận
              </div>
              <div>
                <span
                  class="status-summary__num"
                  :class="statusSummary.missing ? 'text-danger' : ''"
                >
                  {{ statusSummary.missing }}
                </span>
                chưa vào được
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Import chia thành nhiều job; job nào hết hạn lưu trữ hoặc chưa kịp ghi tiến
             độ thì không gộp được, nên số đã xử lý có thể ít hơn tổng gửi đi. -->
        <b-alert
          :show="!importing && progress && progress.processed < progress.total"
          variant="warning"
          class="mt-4"
        >
          Mới xử lý {{ progress ? progress.processed : 0 }}/{{
            progress ? progress.total : 0
          }}
          dòng. Đối chiếu bên dưới lấy từ danh sách tiếp nhận mới là số chính xác.
        </b-alert>

        <!-- Danh sách dòng lỗi -->
        <div
          v-if="progress && progress.failedRows && progress.failedRows.length"
          class="failed-rows mt-5 text-left"
        >
          <h6 class="mb-3">Các dòng lỗi ({{ progress.failedRows.length }})</h6>
          <div class="table-responsive failed-rows__table">
            <table class="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>STT</th>
                  <th>Họ tên</th>
                  <th>Số điện thoại</th>
                  <th>Lý do</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in progress.failedRows" :key="row.rowIndex">
                  <td>{{ row.rowIndex }}</td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.phoneNumber }}</td>
                  <td class="text-danger">
                    <div
                      v-for="(err, idx) in getErrorMessages(row.error)"
                      :key="idx"
                    >
                      - {{ err }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <p v-if="importing" class="text-muted mt-5 mb-0">
          Có thể đóng cửa sổ này, việc import vẫn chạy tiếp. Xong sẽ có thông
          báo khi bạn quay lại màn danh sách.
        </p>

        <div class="d-flex justify-content-center mt-6">
          <b-button
            class="btn btn-success"
            type="button"
            @click="onClickCancelButton"
          >
            {{ importing ? 'Đóng, để chạy tiếp' : 'Đóng' }}
          </b-button>
        </div>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import { EXCEL_FILE_TYPE } from '@/core/plugins/constants';
import { NO_ZALO_GROUP } from '../CustomerReceiveEnum.js';
import Pagination from '@/components/table/pagination';
import ImportPreviewRow from './ImportPreviewRow.vue';

const MAX_FILE_SIZE = 1024 * 1024; // 1MB
const JOB_POLL_INTERVAL = 3000;
const PENDING_JOB_KEY = 'customerReceivesImportJob';

// Hangfire state: Enqueued / Scheduled / Processing / Awaiting / Succeeded / Failed

// Trạng thái trong bảng customer_receives
export const RECEIVE_STATUS = { NEW: 0, JOIN: 1 };

// Cách xử lý nhóm Zalo, áp cho cả danh sách. Mặc định không tạo nhóm vì tạo nhóm
// mới tốn quota và có thể phát sinh chi phí khi hết hạn mức.
const ZALO_MODE = { NONE: 'none', SHARED: 'shared', PRIVATE: 'private' };

// Tạo nhóm Zalo tiêu asset GMF-10 đang có; hết hạn mức thì tự mua thêm của GMF-10,
// 25.000đ một nhóm. Nói thẳng con số thay vì "tốn quota" chung chung để admin cân
// nhắc được trước khi bấm.
const ZALO_GROUP_PRICE = 25000;

function formatVnd(amount) {
  return `${Number(amount || 0).toLocaleString('vi-VN')}đ`;
}

// Lưu kèm danh sách SĐT: đây mới là tập chuẩn để tra kết quả, không bao giờ
// đụng tới bản ghi không liên quan.
// Một lần import bị chia thành nhiều job nhỏ nên lưu MẢNG jobIds.
export function savePendingJob(jobIds, phoneNumbers) {
  localStorage.setItem(
    PENDING_JOB_KEY,
    JSON.stringify({ jobIds, phoneNumbers }),
  );
}

export function getPendingJob() {
  try {
    const raw = localStorage.getItem(PENDING_JOB_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed) return null;

    // Bản ghi cũ lưu một jobId đơn — quy về mảng để chỗ dùng không phải phân nhánh
    if (!parsed.jobIds && parsed.jobId) {
      return { ...parsed, jobIds: [parsed.jobId] };
    }

    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingJob() {
  localStorage.removeItem(PENDING_JOB_KEY);
}
const ZALO_GROUP_PAGE_SIZE = 50; // giới hạn của getgroupsofoa
const ZALO_GROUP_MAX_PAGE = 10;

export default {
  name: 'ModalImport',
  components: {
    Pagination,
    ImportPreviewRow,
  },
  props: {
    coachesList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      step: 'upload', // upload | review | result
      file: null,
      fileError: null,
      loading: false,
      loadingGroups: false,

      allRows: [],
      totalSuccess: 0,

      packages: [],
      zaloGroups: [],
      zaloMode: ZALO_MODE.NONE,
      sharedGroupId: null,
      ZALO_MODE,

      onlyErrors: false,
      // Đã bấm "Kiểm tra dữ liệu" và BE trả kết quả chưa. Chưa thì không cho import,
      // vì bước upload giờ chỉ đọc file chứ không validate gì.
      validated: false,
      downloadingErrors: false,
      autoAccept: false,
      sendNotification: true,
      showWelcome: true,
      importing: false,
      importProgress: { done: 0, total: 0 },
      jobIds: [],
      progress: null,
      pollTimer: null,
      phoneNumbers: [],
      rowStatuses: [],
      paging: {
        total: 0,
        page: 1,
        pageSize: 10,
      },
    };
  },
  computed: {
    modalTitle() {
      if (this.step === 'review') return 'Kiểm tra dữ liệu trước khi import';
      if (this.step === 'progress') return 'Đang import';
      return 'Thêm file';
    },
    fileName() {
      return this.file && this.file.name;
    },
    coachOptions() {
      return this.coachesList || [];
    },
    validRows() {
      return this.allRows.filter((row) => !this.rowHasError(row));
    },
    errorRows() {
      return this.allRows.filter((row) => this.rowHasError(row));
    },
    selectedRows() {
      return this.allRows.filter(
        (row) => row.selected && !this.rowHasError(row),
      );
    },
    dirtyCount() {
      return this.allRows.filter((row) => row.dirty).length;
    },
    allValidSelected() {
      return (
        this.validRows.length > 0 && this.validRows.every((row) => row.selected)
      );
    },
    filteredRows() {
      if (!this.onlyErrors) return this.allRows;
      return this.errorRows;
    },
    // Đối chiếu từ customer_receives — chính xác hơn bộ đếm của job
    sharedGroup() {
      return this.zaloGroups.find((g) => g.group_id === this.sharedGroupId);
    },
    // Ba chế độ nhóm Zalo, mô tả kèm hậu quả để admin không chọn nhầm cái tốn quota
    zaloModeCards() {
      const count = this.selectedRows.length;
      return [
        {
          value: ZALO_MODE.NONE,
          icon: 'b-icon-slash-circle',
          title: 'Không tạo nhóm',
          tag: 'Mặc định',
          desc: 'Không tốn quota. Tạo nhóm sau ở màn Tiếp nhận nếu cần.',
          warn: false,
        },
        {
          value: ZALO_MODE.SHARED,
          icon: 'b-icon-people-fill',
          title: 'Nhóm chung',
          tag: null,
          desc: 'Tất cả khách vào một nhóm có sẵn. Không tạo nhóm mới.',
          warn: false,
        },
        {
          value: ZALO_MODE.PRIVATE,
          icon: 'b-icon-exclamation-triangle-fill',
          title: 'Nhóm riêng',
          // Số tiền lên badge để không lẫn vào phần mô tả
          tag: `Tối đa ${formatVnd(count * ZALO_GROUP_PRICE)}`,
          desc: `Tạo ${count} nhóm mới. Tiêu hạn mức GMF-10 trước, hết mới mua thêm ${formatVnd(
            ZALO_GROUP_PRICE,
          )}/nhóm.`,
          warn: true,
        },
      ];
    },
    // Nhắc lại bằng một dòng đúng những gì sắp chạy, trước khi bấm Import
    importSummary() {
      const parts = [`${this.selectedRows.length} dòng`];

      if (this.zaloMode === ZALO_MODE.NONE) {
        parts.push('không tạo nhóm Zalo');
      } else if (this.zaloMode === ZALO_MODE.SHARED) {
        parts.push(
          this.sharedGroup
            ? `nhóm chung "${this.sharedGroup.name}"`
            : 'nhóm chung (chưa chọn nhóm)',
        );
      } else {
        parts.push(`tạo ${this.selectedRows.length} nhóm riêng`);
      }

      parts.push(this.autoAccept ? 'tiếp nhận luôn' : 'chỉ ghi bản ghi');

      if (this.autoAccept) {
        parts.push(
          this.sendNotification ? 'có gửi thông báo' : 'không gửi thông báo',
        );
        parts.push(this.showWelcome ? 'hiện Welcome' : 'ẩn Welcome');
      }

      return parts.join(' · ');
    },
    statusSummary() {
      if (!this.rowStatuses.length) return null;

      return {
        joined: this.rowStatuses.filter((r) => r.status === RECEIVE_STATUS.JOIN)
          .length,
        imported: this.rowStatuses.filter(
          (r) => r.imported && r.status !== RECEIVE_STATUS.JOIN,
        ).length,
        missing: this.rowStatuses.filter((r) => !r.imported).length,
      };
    },
    missingRows() {
      return this.rowStatuses.filter((r) => !r.imported);
    },
    pagedRows() {
      return this.paginate(
        this.filteredRows,
        this.paging.pageSize,
        this.paging.page,
      );
    },
  },
  watch: {
    filteredRows(rows) {
      this.paging.total = rows.length;
      const lastPage = Math.max(
        1,
        Math.ceil(rows.length / (this.paging.pageSize || 10)),
      );
      if (this.paging.page > lastPage) this.paging.page = lastPage;
    },
    'paging.pageSize'() {
      this.paging.page = 1;
    },
  },
  methods: {
    // ---------------- BƯỚC 1: CHỌN FILE ----------------
    // Xuất đúng các dòng đang lỗi ra file cùng format input + cột NOTE ghi lý do,
    // để admin sửa thẳng trên file rồi nạp lại thay vì dò tay từ file gốc.
    async downloadErrorRows() {
      const rows = this.errorRows;
      if (!rows.length) return;

      try {
        this.downloadingErrors = true;

        const payload = rows.map((row) => ({
          ...this.buildPayload(row),
          onboardGroup: row.onboardGroup ?? null,
          // Gộp lỗi từ BE và lỗi kiểm tại chỗ, mỗi lý do một dòng trong ô NOTE
          note: [...(row.errorMessage || []), ...(row.localErrors || [])].join(
            '\n',
          ),
        }));

        const response = await this.$api.post(
          '/CustomerReceives/ImportCustomerReceives/export-errors',
          payload,
          {
            responseType: 'blob',
            headers: { 'Content-Type': 'application/json' },
          },
        );

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DongLoi_Import_${Date.now()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không tải được file dòng lỗi.',
        });
      } finally {
        this.downloadingErrors = false;
      }
    },
    async downloadFile() {
      try {
        const response = await this.$api.get(
          '/CustomerReceives/download-file',
          {
            responseType: 'blob',
            headers: {
              'Content-Type':
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
          },
        );

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'TemplateImport_DSTiepNhan.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không tải được file mẫu.',
        });
      }
    },
    onUploadFile(event) {
      const file = event.target.files && event.target.files[0];
      this.clearFileInput();
      if (!file) return;

      // Một số máy không cài Office báo MIME rỗng/octet-stream cho .xlsx
      // nên kiểm tra thêm theo đuôi file thay vì chỉ tin MIME.
      const isExcel =
        EXCEL_FILE_TYPE.includes(file.type) ||
        /\.(xlsx|xls)$/i.test(file.name || '');
      if (!isExcel) {
        this.file = null;
        this.fileError = 'Chỉ hỗ trợ file Excel (.xlsx, .xls)';
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        this.file = null;
        this.fileError = 'Kích thước file import tối đa nhỏ hơn 1MB';
        return;
      }

      this.file = file;
      this.fileError = null;
    },
    // Xoá value của input để lần sau chọn lại đúng file vừa sửa vẫn bắn change
    clearFileInput() {
      if (this.$refs.fileUpload) this.$refs.fileUpload.value = null;
    },
    async validateFile() {
      if (!this.file) return;
      try {
        this.loading = true;
        const formData = new FormData();
        formData.append('importFile', this.file);
        // Chỉ đọc file ra danh sách, KHÔNG validate — với file lớn, validate ở bước này
        // khiến admin phải chờ rất lâu mới thấy được gì. Validate là nút bấm riêng.
        const { meta, data, error } = await this.$api.post(
          'CustomerReceives/ImportCustomerReceives/parse',
          formData,
        );
        if (!meta.success) {
          return this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message || 'Không đọc được file import.',
          });
        }

        this.allRows = (data.data || []).map((el, idx) =>
          this.normalizeRow(el, idx),
        );
        this.validated = false;
        this.paging.total = this.allRows.length;
        this.paging.page = 1;
        this.step = 'review';
        // Phải có danh sách gói TRƯỚC khi validate: những dòng chỉ có packageCode
        // cần được resolve sang packageId, nếu không sẽ bị báo thiếu gói tham gia
        // rồi mới tự hết ở lần bấm validate sau.
        await this.loadReviewOptions();
        this.runLocalValidation();
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không kiểm tra được file import.',
        });
      } finally {
        this.loading = false;
      }
    },

    // ---------------- BƯỚC 2: DUYỆT ----------------
    normalizeRow(el, idx) {
      const packageId =
        el.packageId || this.findPackageIdByCode(el.packageCode) || null;
      const errorMessage = this.getErrorMessages(el.errorMessage);
      return {
        rowIndex: el.rowIndex ?? idx + 1,
        number: el.number ?? idx + 1,
        name: el.name ?? null,
        phoneNumber: el.phoneNumber ?? null,
        patientCode: el.patientCode ?? null,
        birthYear: el.birthYear ?? null,
        gender: el.gender ?? null,
        title: el.title ?? null,
        contactSource: el.contactSource ?? null,
        diseaseType: el.diseaseType ?? null,
        packageCode: el.packageCode ?? null,
        packageId,
        coachId: el.coachId ?? el.coach ?? null,
        // Doc tu file Excel, phai giu lai de gui nguoc len BE
        onboardGroup: el.onboardGroup ?? null,
        errorMessage,
        localErrors: [],
        invalidFields: [],
        dirty: false,
        selected: errorMessage.length === 0,
      };
    },
    async loadReviewOptions() {
      await Promise.all([this.loadPackages(), this.loadZaloGroups()]);
      // packageCode -> packageId chỉ resolve được sau khi có danh sách gói
      this.allRows.forEach((row) => {
        if (!row.packageId && row.packageCode) {
          row.packageId = this.findPackageIdByCode(row.packageCode);
        }
      });
    },
    async loadPackages() {
      try {
        const response = await this.$api.get('/Package');
        this.packages = response.data || [];
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không tải được danh sách gói dịch vụ.',
        });
      }
    },
    async loadZaloGroups() {
      try {
        this.loadingGroups = true;
        let offset = 0;
        let total = null;
        let groups = [];
        for (let page = 0; page < ZALO_GROUP_MAX_PAGE; page++) {
          const res = await this.$api.get('/ZaloZnsLog/GetValidGroupOA', {
            params: { offset, count: ZALO_GROUP_PAGE_SIZE },
          });
          const batch = res?.data?.groups || [];
          if (total === null) total = parseInt(res?.data?.total ?? 0, 10) || 0;
          groups = groups.concat(batch);
          offset += ZALO_GROUP_PAGE_SIZE;
          if (!batch.length || groups.length >= total) break;
        }
        this.zaloGroups = groups;
      } catch (error) {
        this.zaloGroups = [];
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không tải được danh sách nhóm Zalo.',
        });
      } finally {
        this.loadingGroups = false;
      }
    },
    findPackageIdByCode(code) {
      if (!code) return null;
      // BE đối chiếu mã gói không phân biệt hoa thường, FE phải khớp theo để
      // không xảy ra cảnh cùng một file mà lúc nhận ra gói lúc không.
      const target = String(code).trim().toLowerCase();
      const found = this.packages.find(
        (pkg) => String(pkg.code).trim().toLowerCase() === target,
      );
      return found ? found.id : null;
    },
    onRowUpdate(row, { field, value }) {
      this.$set(row, field, value);
      if (field === 'packageId') {
        const pkg = this.packages.find((p) => p.id === value);
        this.$set(row, 'packageCode', pkg ? pkg.code : null);
      }
      // Lỗi cũ của BE không còn đáng tin sau khi sửa -> bắt buộc kiểm tra lại
      if (field !== 'zaloGroupId') this.$set(row, 'dirty', true);
      this.validateRowLocal(row);
    },
    onRowToggle(row, checked) {
      this.$set(row, 'selected', checked);
    },
    toggleAll(checked) {
      this.validRows.forEach((row) => this.$set(row, 'selected', checked));
    },
    rowHasError(row) {
      return (
        (row.localErrors && row.localErrors.length > 0) ||
        (row.errorMessage && row.errorMessage.length > 0)
      );
    },
    // FE chỉ bắt lỗi định dạng chắc chắn sai; luật nghiệp vụ (trùng SĐT trong DB,
    // mã gói, coach...) vẫn do BE quyết định qua validate/validate-rows.
    validateRowLocal(row) {
      const errors = [];
      const invalidFields = [];

      if (!row.name || !String(row.name).trim()) {
        errors.push('Chưa có họ tên');
        invalidFields.push('name');
      }

      const phone = String(row.phoneNumber || '').trim();
      const patientCode = String(row.patientCode || '').trim();
      if (!phone && !patientCode) {
        errors.push('Chưa có số điện thoại hoặc Mã bệnh nhân');
        invalidFields.push('phoneNumber');
      } else if (phone && !/^0\d{9}$/.test(phone)) {
        errors.push(
          'Số điện thoại không đúng định dạng (10 số, bắt đầu bằng 0)',
        );
        invalidFields.push('phoneNumber');
      } else if (phone && this.isDuplicatedInFile(row, phone)) {
        errors.push('Số điện thoại bị trùng với dòng khác trong file');
        invalidFields.push('phoneNumber');
      }

      if (row.birthYear) {
        const year = Number(row.birthYear);
        const thisYear = new Date().getFullYear();
        if (!Number.isInteger(year) || year < 1900 || year > thisYear) {
          errors.push('Năm sinh không hợp lệ');
          invalidFields.push('birthYear');
        }
      }

      if (!row.diseaseType) {
        errors.push('Chưa chọn phân loại bệnh');
        invalidFields.push('diseaseType');
      }

      this.$set(row, 'localErrors', errors);
      this.$set(row, 'invalidFields', invalidFields);
      if (errors.length) this.$set(row, 'selected', false);
    },
    isDuplicatedInFile(row, phone) {
      return this.allRows.some(
        (other) =>
          other !== row && String(other.phoneNumber || '').trim() === phone,
      );
    },
    runLocalValidation() {
      this.allRows.forEach((row) => this.validateRowLocal(row));
    },
    buildPayload(row) {
      const shared = this.zaloMode === ZALO_MODE.SHARED;
      const noGroup = this.zaloMode === ZALO_MODE.NONE;
      return {
        rowIndex: row.rowIndex,
        number: row.number,
        name: row.name,
        phoneNumber: row.phoneNumber,
        patientCode: row.patientCode ?? null,
        birthYear: row.birthYear ? Number(row.birthYear) : null,
        gender: row.gender,
        title: row.title,
        contactSource: row.contactSource,
        diseaseType: row.diseaseType,
        packageId: row.packageId,
        coachId: row.coachId,
        onboardGroup: row.onboardGroup ?? null,
        // Khách vẫn "có dùng nhóm Zalo" khi gán nhóm chung - chỉ là không tạo nhóm mới
        useZaloGroup: !noGroup,
        zaloGroupId: noGroup
          ? NO_ZALO_GROUP
          : shared
          ? this.sharedGroupId
          : null,
        // BE lưu thẳng link này vào record, khỏi phải gọi lại Zalo để tra
        zaloGroupLink: shared ? this.sharedGroup?.group_link ?? null : null,
        // Cờ bypass: KHÔNG tạo nhóm mới, KHÔNG trừ quota.
        // Các bước còn lại (tạo tài khoản nếu chưa có, kích hoạt gói) vẫn chạy bình thường.
        bypassCreateZaloGroup: shared || noGroup,
        isSharedZaloGroup: shared,
      };
    },
    async revalidateRows() {
      try {
        this.loading = true;
        const { meta, data, error } = await this.$api.post(
          'CustomerReceives/ImportCustomerReceives/validate-rows',
          this.allRows.map((row) => this.buildPayload(row)),
          { headers: { 'Content-Type': 'application/json' } },
        );
        if (!meta.success) {
          return this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message || 'Không kiểm tra lại được dữ liệu.',
          });
        }
        this.mergeValidationResult(data.data || []);
        // Mở khoá nút Import — trước bước này dữ liệu chưa được BE kiểm tra lần nào
        this.validated = true;
        this.$toastr.s({
          title: 'Đã kiểm tra xong',
          msg: `${this.validRows.length}/${this.allRows.length} dòng hợp lệ.`,
        });
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: error.message || 'Không kiểm tra lại được dữ liệu.',
        });
      } finally {
        this.loading = false;
      }
    },
    mergeValidationResult(rows) {
      rows.forEach((result) => {
        const row = this.allRows.find((r) => r.rowIndex === result.rowIndex);
        if (!row) return;
        this.$set(
          row,
          'errorMessage',
          this.getErrorMessages(result.errorMessage),
        );
        this.$set(row, 'dirty', false);
        this.validateRowLocal(row);
        if (!this.rowHasError(row)) this.$set(row, 'selected', true);
      });
    },
    // Đẩy vào hàng đợi Hangfire rồi poll tiến độ. Chạy nền nên đóng tab vẫn tiếp tục;
    // lần sau vào màn Danh sách tiếp nhận sẽ được toast tổng kết.
    async confirmImport() {
      const rows = this.selectedRows;
      if (!rows.length) return;

      if (this.zaloMode === ZALO_MODE.SHARED && !this.sharedGroupId) {
        return this.$toastr.e({
          title: 'Thiếu thông tin',
          msg: 'Chọn nhóm Zalo chung trước khi import.',
        });
      }

      const confirmed = await this.confirmBeforeImport(rows);
      if (!confirmed) return;

      try {
        this.loading = true;
        const { meta, data, error } = await this.$api.post(
          'CustomerReceives/ImportCustomerReceives/enqueue',
          {
            autoAccept: this.autoAccept,
            sendNotification: this.sendNotification,
            showWelcome: this.autoAccept ? this.showWelcome : true,
            rows: rows.map((row) => this.buildPayload(row)),
          },
          { headers: { 'Content-Type': 'application/json' } },
        );

        if (!meta.success) {
          return this.$toastr.e({
            title: 'Lỗi !',
            msg: error.message || 'Không đẩy được vào hàng đợi.',
          });
        }

        // BE chia lần import thành nhiều job nhỏ, mỗi job tối đa 50 dòng
        this.jobIds = data.jobIds || [];
        this.importProgress = { done: 0, total: data.total ?? rows.length };
        this.importing = true;
        this.step = 'progress';

        // Lưu lại để nếu admin đóng modal giữa chừng, lần sau vào màn vẫn báo kết quả
        this.phoneNumbers = rows.map((row) => row.phoneNumber);
        savePendingJob(this.jobIds, this.phoneNumbers);
        this.startPollingJob();
      } catch (err) {
        this.$toastr.e({
          title: 'Lỗi !',
          msg: err.message || 'Không đẩy được vào hàng đợi.',
        });
      } finally {
        this.loading = false;
      }
    },
    startPollingJob() {
      this.stopPollingJob();
      this.pollTimer = setInterval(this.pollJob, JOB_POLL_INTERVAL);
      this.pollJob();
    },
    stopPollingJob() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async pollJob() {
      if (!this.jobIds.length) return;
      try {
        // Gộp tiến độ của tất cả job thuộc lần import này
        const { meta, data } = await this.$api.post(
          'CustomerReceives/ImportCustomerReceives/jobs-progress',
          this.jobIds,
          { headers: { 'Content-Type': 'application/json' } },
        );
        if (!meta.success || !data) return;

        this.progress = data;
        this.importProgress = {
          done: data.processed || 0,
          total: data.total || this.importProgress.total,
        };
        this.totalSuccess = data.success || 0;

        // Xong khi MỌI job đã chạy hết, không phải chỉ job đầu tiên
        if (data.finished) {
          this.stopPollingJob();
          this.importing = false;
          clearPendingJob();
          await this.loadRowStatuses();
          this.$emit('import-success');
        }
      } catch {
        // Mạng chập một nhịp thì bỏ qua, lần poll sau lấy lại
      }
    },
    // Bảng nhãn/giá trị thay cho mấy câu văn xuôi căn giữa - mắt quét nhanh hơn,
    // và tô đỏ đúng hai lựa chọn dễ gây hậu quả: tạo nhóm mới và tắt gửi tin.
    buildConfirmContent(rows) {
      const esc = (value) =>
        String(value ?? '').replace(
          /[&<>"]/g,
          (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]),
        );

      const items = [];

      if (this.autoAccept) {
        items.push(['Tiếp nhận', 'Tạo tài khoản, kích hoạt gói', false]);

        if (this.zaloMode === ZALO_MODE.PRIVATE) {
          items.push([
            'Nhóm Zalo',
            `Tạo ${rows.length} nhóm mới — tiêu hạn mức GMF-10, hết thì mua thêm ` +
              `${formatVnd(ZALO_GROUP_PRICE)}/nhóm (tối đa ${formatVnd(
                rows.length * ZALO_GROUP_PRICE,
              )})`,
            true,
          ]);
        } else if (this.zaloMode === ZALO_MODE.SHARED) {
          items.push([
            'Nhóm Zalo',
            `Nhóm chung: ${esc(this.sharedGroup?.name ?? '')}`,
            false,
          ]);
        } else {
          items.push(['Nhóm Zalo', 'Không tạo nhóm', false]);
        }

        items.push(
          this.sendNotification
            ? ['Thông báo', 'Gửi cho khách chưa có tài khoản', false]
            : ['Thông báo', 'Không gửi — khách không nhận được thông tin đăng nhập', true],
        );

        items.push([
          'Welcome',
          this.showWelcome ? 'Hiện trên app' : 'Ẩn trên app',
          false,
        ]);
      } else {
        items.push(['Tiếp nhận', 'Chỉ ghi bản ghi, chưa tiếp nhận', false]);
      }

      const rowsHtml = items
        .map(
          ([label, value, warn]) => `
            <div style="display:flex;gap:12px;padding:7px 0;border-top:1px solid #eef0f4">
              <span style="flex:0 0 92px;color:#7c8b9a;text-align:left">${label}</span>
              <span style="flex:1;text-align:left;color:${
                warn ? '#d9534f' : '#1b1b28'
              };font-weight:${warn ? 600 : 400}">${value}</span>
            </div>`,
        )
        .join('');

      const el = document.createElement('div');
      el.innerHTML = `
        <div style="font-size:13px;line-height:1.5">
          <div style="font-size:15px;font-weight:600;margin-bottom:4px">
            Import ${rows.length} dòng
          </div>
          ${rowsHtml}
        </div>`;
      return el;
    },
    confirmBeforeImport(rows) {
      return new Promise((resolve) => {
        this.$swal({
          title: 'Xác nhận import',
          content: this.buildConfirmContent(rows),
          icon:
            this.autoAccept && this.zaloMode === ZALO_MODE.PRIVATE
              ? 'warning'
              : 'info',
          buttons: {
            cancel: {
              text: 'Quay lại',
              value: false,
              visible: true,
              className: 'btn btn-secondary',
              closeModal: true,
            },
            confirm: {
              text: 'Tiếp tục',
              value: true,
              visible: true,
              className: 'btn btn-success',
              closeModal: true,
            },
          },
        }).then((result) => resolve(!!result));
      });
    },
    backToUpload() {
      this.step = 'upload';
      this.allRows = [];
      this.validated = false;
      this.file = null;
      this.fileError = null;
      this.clearFileInput();
    },

    // ---------------- CHUNG ----------------
    // Nguồn chính xác và vĩnh viễn: tra thẳng bảng customer_receives theo đúng
    // danh sách SĐT đã gửi. Không phụ thuộc hạn lưu trữ job của Hangfire, và không
    // sai kể cả khi job bị chạy lại.
    async loadRowStatuses() {
      if (!this.phoneNumbers.length) return;
      try {
        const { meta, data } = await this.$api.post(
          'CustomerReceives/ImportCustomerReceives/status',
          this.phoneNumbers,
          { headers: { 'Content-Type': 'application/json' } },
        );
        if (meta.success) this.rowStatuses = data || [];
      } catch {
        // Không tra được thì vẫn còn số liệu từ job
      }
    },
    resetModal() {
      this.step = 'upload';
      this.file = null;
      this.fileError = null;
      this.clearFileInput();
      this.allRows = [];
      this.validated = false;
      this.totalSuccess = 0;
      this.bulkZaloGroupId = null;
      this.onlyErrors = false;
      this.autoAccept = false;
      this.sendNotification = true;
      this.showWelcome = true;
      this.importing = false;
      this.importProgress = { done: 0, total: 0 };
      this.jobIds = [];
      this.progress = null;
      this.phoneNumbers = [];
      this.rowStatuses = [];
      this.stopPollingJob();
      this.paging.page = 1;
      this.paging.total = 0;
      this.$emit('reset');
    },
    onClickCancelButton() {
      this.$bvModal.hide('modal-import');
    },
    // BE có thể kết thúc bằng '\n' hoặc không -> lọc chuỗi rỗng thay vì pop()
    getErrorMessages(text) {
      if (!text) return [];
      if (Array.isArray(text)) return text.filter(Boolean);
      return text
        .split('\n')
        .map((msg) => msg.trim())
        .filter(Boolean);
    },
  },
};
</script>

<style lang="scss" scoped>
.wrap__file {
  padding: 10px 16px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.wrap__icon {
  background: #e4f5f5;
  border-radius: 8px;
  width: 44px;
  height: 44px;
  margin-right: 16px;
  display: flex;
  justify-content: center;
  align-items: center;

  &--error {
    background: #ffebef;
  }
}

.bulk-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f7f9fa;
  border-radius: 6px;

  &__group {
    display: flex;
    align-items: center;
  }
}

.bulk-select {
  width: auto;
  min-width: 260px;
}

.import-options {
  padding: 16px;
  background: #f7f9fa;
  border-radius: 6px;

  &__label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7c8b9a;
    margin-bottom: 8px;
  }

  &__summary {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px dashed #dfe6ec;
    font-size: 13px;
    color: #4a5b6b;
  }
}

.zalo-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.zalo-card {
  position: relative;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #dfe6ec;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #21a567;
  }

  &:focus-visible {
    outline: 2px solid #21a567;
    outline-offset: 2px;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  &__icon {
    font-size: 20px;
    color: #7c8b9a;
  }

  &__check {
    font-size: 18px;
    color: #21a567;
  }

  &__title {
    font-weight: 700;
    margin-bottom: 4px;
  }

  // Không uppercase: badge của "Nhóm riêng" chứa số tiền, viết hoa thành "125.000Đ"
  &__tag {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
    padding: 1px 6px;
    border-radius: 3px;
    background: #eef1f4;
    color: #7c8b9a;
    vertical-align: middle;
    margin-left: 4px;
  }

  &__desc {
    font-size: 12px;
    color: #7c8b9a;
    line-height: 1.5;
  }

  &--active {
    border-color: #21a567;
    box-shadow: 0 0 0 1px #21a567;

    .zalo-card__icon {
      color: #21a567;
    }
  }

  // Chế độ tốn quota: giữ màu cảnh báo kể cả khi chưa chọn
  &--warn {
    .zalo-card__icon {
      color: #f0a32e;
    }

    .zalo-card__tag {
      background: #fff4e0;
      color: #d98b0f;
    }

    &:hover {
      border-color: #f0a32e;
    }

    &.zalo-card--active {
      border-color: #f0a32e;
      box-shadow: 0 0 0 1px #f0a32e;

      .zalo-card__icon {
        color: #f0a32e;
      }
    }
  }
}

.shared-picker {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
}

.opt-check {
  display: flex;
  align-items: flex-start;
  margin: 0 0 8px;
  cursor: pointer;

  input {
    margin: 3px 10px 0 0;
    flex-shrink: 0;
  }

  b {
    display: block;
    font-weight: 600;
  }

  small {
    display: block;
    color: #7c8b9a;
  }

  &--nested {
    margin-left: 26px;
  }

  &--off {
    cursor: not-allowed;

    b,
    small {
      color: #a9b4bf;
    }
  }
}

.status-summary {
  display: flex;
  justify-content: space-around;
  padding: 14px;
  background: #f7f9fa;
  border-radius: 6px;

  &__num {
    font-size: 20px;
    font-weight: 700;
    margin-right: 4px;
  }
}

.failed-rows__table {
  max-height: 40vh;
  overflow-y: auto;

  thead th {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 2;
  }
}

.review-table {
  max-height: 55vh;
  overflow-y: auto;

  thead th {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 2;
    white-space: nowrap;
  }
}

.status {
  padding: 6px 12px;
  border-radius: 20px;
  color: #fff;
  text-transform: capitalize;
  text-align: center;
  width: 110px;

  &.active {
    background: #21a567;
  }

  &.inactive {
    background: #ff5756;
  }
}

.badge-no-zalo {
  display: inline-flex;
  align-items: center;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(139, 92, 246, 0.12);
  color: #7c3aed;
  border: 1px solid rgba(139, 92, 246, 0.3);
  vertical-align: middle;
  margin-left: 4px;
  white-space: nowrap;
}

.badge-shared-group {
  display: inline-flex;
  align-items: center;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(37, 154, 187, 0.12);
  color: #1b7f9c;
  border: 1px solid rgba(37, 154, 187, 0.35);
  vertical-align: middle;
  margin-left: 4px;
  white-space: nowrap;
}
</style>

<style lang="scss">
#modal-import {
  .modal-dialog.modal-xl {
    max-width: 1500px;
  }
}
</style>
